import {
  BodyText,
  Card,
  CardContent,
  Checkbox,
  Divider,
  Flex,
  Heading,
  ValidationText,
} from '@utilitywarehouse/hearth-react';
import { TickSmallIcon } from '@utilitywarehouse/hearth-react-icons';
import { useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';

import { DocumentLink } from '@components/DocumentLink';
import { InsuranceDocument } from '@generated/gql/graphql';
import { useTrackingContext } from '@tracking/Provider/Context';
import { useTrackSignupBhcDocumentClicked } from '@tracking/trackingEvents';

import { BHCLegalSectionValues } from '../validationSchema';
import { getDeclarations } from './constants';
import './styles.scss';

interface BHCLegalDeclarationsProps {
  text: string;
}

const BHCLegalDeclarations: React.FC<BHCLegalDeclarationsProps> = ({
  text,
}) => {
  return (
    <div className="bhc-legal__declaration">
      <TickSmallIcon />
      <BodyText> {text}</BodyText>
    </div>
  );
};

const BHCLegalCheckbox = () => {
  const { values, handleChange, handleBlur, setFieldValue } =
    useFormikContext<BHCLegalSectionValues>();

  const {
    errors: { hasAcceptedInformation: hasAcceptedInformationError },
  } = useFormikContext<BHCLegalSectionValues>();

  return (
    <Flex spacing="md" direction="column">
      <Checkbox
        data-testid="hasAcceptedInformation"
        id="hasAcceptedInformation"
        name="hasAcceptedInformation"
        checked={values.hasAcceptedInformation}
        onChange={handleChange}
        onBlur={handleBlur}
        onCheckedChange={(checked) => {
          setFieldValue('hasAcceptedInformation', checked);
        }}
        label="I’ve read the above statements and policy documents, and confirm the information provided is correct."
      />
      {hasAcceptedInformationError && (
        <Flex gap="100" className="bhc-legal__alert">
          <ValidationText
            data-testid="hasAcceptedInformation-error"
            status="invalid"
          >
            {hasAcceptedInformationError}
          </ValidationText>
        </Flex>
      )}
    </Flex>
  );
};

const BHCLegal = ({ documents }: { documents: InsuranceDocument[] }) => {
  const { trackingContexts } = useTrackingContext();
  const trackDocClick = useTrackSignupBhcDocumentClicked();

  const declarations = getDeclarations();

  const handleDocClick = (documentTitle: string) => {
    trackDocClick(
      {
        document_title: documentTitle,
      },
      { contexts: trackingContexts }
    );
  };

  return (
    <>
      <Flex spacing="2xl" direction="column">
        <Flex spacing="lg" direction="column">
          <Heading as="h2" size="lg">
            Policy documents
          </Heading>
          <BodyText>
            We'll send you an email with your personalised documents when your
            policy starts.
          </BodyText>
          <Flex asChild spacing="lg" direction="column" alignItems="start">
            <div
              className="bhc-legal__links"
              data-testid="boiler-home-cover-documents"
            >
              {documents.map((item) => (
                <DocumentLink
                  key={item.name}
                  name={item.name}
                  url={item.url}
                  onClick={handleDocClick}
                />
              ))}
            </div>
          </Flex>
        </Flex>

        <Card
          variant="emphasis"
          colorScheme="neutralStrong"
          className="bhc-legal__declarations"
        >
          <CardContent direction="column" spacing="xl">
            <Flex gap="200" direction="column">
              <Heading as="h2" size="lg">
                Declarations
              </Heading>

              <div className="bhc-legal__declarations">
                {declarations.map((item) => (
                  <BHCLegalDeclarations text={item} key={uuidv4()} />
                ))}
              </div>
            </Flex>

            <Divider />

            <BHCLegalCheckbox />
          </CardContent>
        </Card>
      </Flex>
    </>
  );
};

export { BHCLegal };
